import { useState } from "react";
import { toast } from "react-toastify";
import Axios from "axios";
import { api_url } from "../../../config";
import {
  COLONNE_NIR,
  CSV_DELIMITER,
  colonnesManquantes,
  convertToCSV,
  downloadCSV,
  parseCSV,
} from "./csvRelanceRdo";

export { CSV_DELIMITER };

// ===== CONSTANTES DE TYPAGE =====
// Structure d'une ligne du CSV déposé : le NIR suffit
export const CSV_ALLOCATAIRE_FIELDS = {
  nir: COLONNE_NIR,
};

// Structure d'un allocataire après pré-validation API.
// Nom, prénom et date de naissance viennent de la base, pas du fichier.
export const VALIDATION_ALLOCATAIRE_FIELDS = {
  ...CSV_ALLOCATAIRE_FIELDS,
  nom: "nom",
  prenom: "prenom",
  dateNaissance: "dateNaissance",
  email: "email", // email de l'allocataire trouvé en BDD
  statut: "statut", // "valide" | "erreur"
  erreur: "erreur", // string ou null
  orientation: "orientation", // nom de l'orientation
  dateOrientation: "dateOrientation", // date d'orientation
  typeDroitsDevoirs: "typeDroitsDevoirs", // "SDD" | "NSDD"
};

// Statuts possibles
export const STATUTS = {
  VALIDE: "valide",
  ERREUR: "erreur",
};

// ===== HOOK PRINCIPAL =====
const useRDOInvite = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validatedData, setValidatedData] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [step, setStep] = useState("upload"); // "upload" | "validation" | "sent"

  // États pour la pagination et filtres du tableau de validation
  const [validationPage, setValidationPage] = useState(1);
  const [validationFilter, setValidationFilter] = useState("tous"); // "tous" | "valide" | "erreur"
  const validationItemsPerPage = 30;

  // Télécharger le gabarit CSV
  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/gabarit_invitation_rdo.csv";
    link.download = "gabarit_invitation_rdo.csv";
    link.click();
  };

  // Gérer l'upload du fichier
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast.error("Le fichier doit être au format CSV");
      return;
    }

    setUploadedFile(file);
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const parsedData = parseCSV(event.target.result);

        if (parsedData.length === 0) {
          toast.error(
            "Le fichier ne contient aucun NIR : il faut une ligne d'en-tête « nir » puis un NIR par ligne"
          );
          return;
        }

        const manquantes = colonnesManquantes(parsedData[0]);
        if (manquantes.length > 0) {
          toast.error(
            `Colonne manquante : ${manquantes.join(", ")}. Le fichier doit contenir une colonne « nir » avec un NIR à 15 chiffres par ligne`
          );
          return;
        }

        setCsvData(parsedData);
        toast.success(`${parsedData.length} NIR chargé(s)`);
      } catch (error) {
        toast.error("Erreur lors de la lecture du fichier : " + error.message);
      }
    };

    reader.readAsText(file);
  };

  // Pré-valider les données
  const handlePreValidation = async () => {
    if (csvData.length === 0) {
      toast.error("Aucune donnée à valider");
      return;
    }

    if (!uploadedFile) {
      toast.error("Fichier introuvable");
      return;
    }

    setIsValidating(true);
    try {
      // Préparer le FormData pour l'upload
      const formData = new FormData();
      formData.append("file", uploadedFile);

      // Appel à l'API de pré-validation avec Axios
      const response = await Axios.post(
        api_url + "rdo/relances/validation",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          responseType: "text", // L'API renvoie du CSV (texte)
        }
      );

      // L'API renvoie un CSV : une ligne par NIR lu, valide ou en erreur
      const parsedValidatedData = parseCSV(response.data);

      if (parsedValidatedData.length === 0) {
        toast.error("Aucune ligne exploitable dans le fichier");
        return;
      }

      setValidatedData(parsedValidatedData);
      setStep("validation");

      const validCount = parsedValidatedData.filter(
        (d) => d.statut === STATUTS.VALIDE
      ).length;
      const errorCount = parsedValidatedData.filter(
        (d) => d.statut === STATUTS.ERREUR
      ).length;

      toast.info(
        `Validation terminée : ${validCount} valide(s), ${errorCount} en erreur`
      );
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Erreur lors de la validation";
      toast.error(errorMessage);
    } finally {
      setIsValidating(false);
    }
  };

  // Télécharger les données de pré-validation
  const handleDownloadValidation = () => {
    // Le NIR en premier pour permettre la réutilisation comme fichier d'import
    const fields = [
      { name: "nir", path: VALIDATION_ALLOCATAIRE_FIELDS.nir },
      // Colonnes supplémentaires pour information
      { name: "nom", path: VALIDATION_ALLOCATAIRE_FIELDS.nom },
      { name: "prenom", path: VALIDATION_ALLOCATAIRE_FIELDS.prenom },
      {
        name: "dateNaissance",
        path: VALIDATION_ALLOCATAIRE_FIELDS.dateNaissance,
      },
      { name: "email", path: VALIDATION_ALLOCATAIRE_FIELDS.email },
      { name: "orientation", path: VALIDATION_ALLOCATAIRE_FIELDS.orientation },
      {
        name: "dateOrientation",
        path: VALIDATION_ALLOCATAIRE_FIELDS.dateOrientation,
      },
      {
        name: "typeDroitsDevoirs",
        path: VALIDATION_ALLOCATAIRE_FIELDS.typeDroitsDevoirs,
      },
      { name: "statut", path: VALIDATION_ALLOCATAIRE_FIELDS.statut },
      { name: "erreur", path: VALIDATION_ALLOCATAIRE_FIELDS.erreur },
    ];

    const csvContent = convertToCSV(validatedData, fields);
    downloadCSV(csvContent, "validation_invitations_rdo.csv");
    toast.success(
      "Fichier de validation téléchargé - Peut être réutilisé comme fichier d'import"
    );
  };

  // Envoyer les invitations (import du fichier CSV)
  const handleSendInvitations = async () => {
    const validAllocataires = validatedData.filter(
      (d) => d.statut === STATUTS.VALIDE
    );

    if (validAllocataires.length === 0) {
      toast.error("Aucun allocataire valide à inviter");
      return;
    }

    if (!uploadedFile) {
      toast.error("Fichier CSV introuvable");
      return;
    }

    setIsSending(true);
    try {
      // Préparer le FormData pour l'upload
      const formData = new FormData();
      formData.append("file", uploadedFile);

      // Appel à l'API d'envoi des invitations avec Axios
      const response = await Axios.post(
        api_url + "rdo/relances/envoyer",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setStep("sent");

      // Afficher le message de succès avec les informations retournées par l'API
      const { numeroLot, nbAllocataires } = response.data;
      toast.success(
        `Import en cours - Lot ${numeroLot} créé avec ${nbAllocataires} allocataire(s)`
      );
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Erreur lors de l'import";
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  // Réinitialiser le formulaire
  const handleReset = () => {
    window.location.reload();
  };

  // Calculer les compteurs
  const validCount = validatedData.filter(
    (d) => d.statut === STATUTS.VALIDE
  ).length;
  const errorCount = validatedData.filter(
    (d) => d.statut === STATUTS.ERREUR
  ).length;

  // Filtrer les données selon le filtre sélectionné
  const getFilteredValidatedData = () => {
    if (validationFilter === "valide") {
      return validatedData.filter((d) => d.statut === STATUTS.VALIDE);
    }
    if (validationFilter === "erreur") {
      return validatedData.filter((d) => d.statut === STATUTS.ERREUR);
    }
    return validatedData; // "tous"
  };

  const filteredValidatedData = getFilteredValidatedData();

  // Calculer les données paginées
  const validationNbPages = Math.ceil(
    filteredValidatedData.length / validationItemsPerPage
  );
  const paginatedValidatedData = filteredValidatedData.slice(
    (validationPage - 1) * validationItemsPerPage,
    validationPage * validationItemsPerPage
  );

  // Changer de page
  const handleValidationPageChange = (page) => {
    setValidationPage(page);
  };

  // Changer le filtre
  const handleValidationFilterChange = (filter) => {
    setValidationFilter(filter);
    setValidationPage(1); // Retour à la page 1 lors du changement de filtre
  };

  return {
    // État
    uploadedFile,
    csvData,
    validatedData,
    isValidating,
    isSending,
    step,
    validCount,
    errorCount,

    // Pagination et filtres
    validationPage,
    validationFilter,
    validationNbPages,
    paginatedValidatedData,
    filteredValidatedData,
    validationItemsPerPage,

    // Actions
    handleDownloadTemplate,
    handleFileUpload,
    handlePreValidation,
    handleDownloadValidation,
    handleSendInvitations,
    handleReset,
    handleValidationPageChange,
    handleValidationFilterChange,
  };
};

export default useRDOInvite;
